import React, {useCallback, useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import { useBuildingKey } from "../../Helpers/utils";
import {translate} from "../../types/EnumTranslations";
import "./LevelPage.css";
import BuildingCustomButton from "../BuildingDetails/BuildingCustomButton";
import { Helmet } from "react-helmet-async";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import InfoPanel from "../../Components/InfoPanel";
import {RoomImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {LevelModel} from "../../types/protocols";

const LevelPage: React.FC = () => {
    const navigate = useNavigate();
    const buildingKey = useBuildingKey();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const [hoveredRoomKey, setHoveredRoomKey] = useState<string | null>(null);
    const [level, setLevel] = useState<LevelModel|undefined>(undefined);
    const location = useLocation();
    const pathSegments = location.pathname.split("/");
    const levelKey = pathSegments[pathSegments.length - 1];

    const { currentBuilding, error, loading, fetchBuilding } = useBuilding();

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    useEffect(() => {
        if (currentBuilding && levelKey) {
            setLevel(currentBuilding.model.structure?.levels?.[levelKey]);
        }
    }, [currentBuilding, levelKey]);

    const calculateMarkerPosition = useCallback((room: RoomImpl) => {
        const { x = 0, y = 0, width: roomWidth, height: roomHeight } = room.location || {};
        if (roomWidth === undefined || roomHeight === undefined) return null;

        const { width: containerWidth, height: containerHeight } = containerDimensions;
        if (!containerWidth || !containerHeight) return null;

        const scale = containerWidth / roomWidth;

        const iconSize = Math.min(containerWidth, containerHeight) * 0.05;

        const scaledX = x * scale;
        const scaledY = y * scale;

        return {
            left: `${scaledX}px`,
            top: `${scaledY}px`,
            width: `${iconSize}px`,
            height: `${iconSize}px`
        };
    }, [containerDimensions]);

    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', updateDimensions);

        updateDimensions();

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);

    if (loading) return <></>;
    if (error) return <p>{error}</p>;
    if (!currentBuilding) return <p>Building data missing</p>;
    if (!level) return <>Level data missing</>;

    const rooms: RoomImpl[] = currentBuilding.model.structure?.rooms
        ? Object.values(currentBuilding.model.structure.rooms).filter(
            (room) => room.levelKey === level.key
        ).map((item) => new RoomImpl(item))
        : [];

    const handleMarkerClick = (roomKey: string) => {
        navigate(`/building/${buildingKey}/room/${roomKey}`);
    };

    const handleMouseEnter = (roomKey: string) => {
        setHoveredRoomKey(roomKey);
    };

    const handleMouseLeave = () => {
        setHoveredRoomKey(null);
    };

    return (
        <>
            <BreadcrumbComponent
                items={[
                    {text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
                    {text: `Våning ${level.number}`}
                ]}
            />

            <div style={{ padding: "20px", maxWidth: '1500px'}}>
                <Helmet>
                    <title>{`Våning ${level.number}: ${"building.name"}`}</title>
                </Helmet>

                <h3 style={{marginBottom: '20px'}}>
                    Våning {level.number}
                </h3>
                <div
                    style={{
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '40px',
                        marginBottom: '20px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '60px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                >

                    <div
                        ref={containerRef}
                        className="blueprint-container"
                        style={{
                            flex: "2",
                            height: 'fit-content',
                            position: 'relative',
                            minWidth: '300px'
                        }}
                    >
                        <h4 style={{marginBottom: '20px'}}>Ritning</h4>

                        {level.planImage ?
                            <img
                                src={level.planImage.fullSizeUrl}
                                alt="Blueprint"
                                className="blueprint-image"
                                onLoad={() => {
                                    if (containerRef.current) {
                                        setContainerDimensions({
                                            width: containerRef.current.offsetWidth,
                                            height: containerRef.current.offsetHeight
                                        });
                                    }
                                }}
                                style={{
                                    boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                                    borderRadius: '10px',
                                    width: '100%',
                                    height: 'auto'
                                }}
                            />
                            :
                            <>Ritning saknas</>
                        }


                        {rooms.map((room) => {
                            const position = calculateMarkerPosition(room);
                            if (!position) return null;

                            const isHovered = hoveredRoomKey === room.key;

                            return (
                                <div
                                    key={room.key}
                                    className={`marker ${isHovered ? "highlight-marker" : ""}`}
                                    style={position}
                                    onClick={() => handleMarkerClick(room.key)}
                                    onMouseEnter={() => handleMouseEnter(room.key)}
                                    onMouseLeave={handleMouseLeave}
                                >
                            <span className="tooltip">
                                {room.displayName()}
                            </span>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ flex: "1" }}>
                        <h4 style={{marginBottom: '20px'}}>Rum</h4>
                        {rooms.length > 0 ? (
                            <div>
                                {rooms
                                    .sort((roomA, roomB) => {
                                        if (roomA.number && roomB.number) {
                                            const numA = parseFloat(roomA.number);
                                            const numB = parseFloat(roomB.number);

                                            if (!isNaN(numA) && !isNaN(numB)) {
                                                return numA - numB;
                                            }
                                        }
                                        if (roomA.number && !roomB.number) return -1;
                                        if (!roomA.number && roomB.number) return 1;
                                        return roomA.key.localeCompare(roomB.key);
                                    })
                                    .map((room) => (
                                        <React.Fragment key={room.key}>
                                            <BuildingCustomButton
                                                title={room.displayName()}
                                                metadata={[translate(room.type), `${"room.area ?? 0"} kvm `]}
                                                imagePath={`RoomTypes/${room?.type || "room_general"}.png`}
                                                onClick={() => handleMarkerClick(room.key)}
                                                onMouseEnter={() => handleMouseEnter(room.key)}
                                                onMouseLeave={handleMouseLeave}
                                                isHovered={hoveredRoomKey === room.key}
                                            />
                                        </React.Fragment>
                                    ))}
                            </div>
                        ) : (
                            <>Detta våningsplan innehåller inga rum</>
                        )}
                    </div>

                    <div style={{ flex: '1' }}>
                        <h4 style={{marginBottom: '20px'}}>Fakta</h4>
                        <InfoPanel items={[
                            {label: 'Våning', value: level.number},
                            {label: 'Eget namn', value: level.name || '-'},
                            {label: 'Antal rum', value: rooms.length},
                            {label: 'Total area', value: "-"},
                            {label: 'Fastighetsteknik', value: "x st"
                            },
                        ]}/>

                    </div>


                </div>
            </div>
        </>
    );
};


export default LevelPage;